import { faker } from "@faker-js/faker";
import {
    formatPhoneNumber,
    UnifiedPriorityEntryInput
} from "@therapprove/common";

// Define the structure of a waitlist entry
interface WaitlistRow {
    currentPastSiblingOfClient?: string | null;
    pt?: string | null;
    ot?: string | null;
    st?: string | null;
    referralDate: string;
    patientName: string;
    dob: string;
    weight?: string | null;
    caregivers: string;
    emails: string;
    phoneNumbers: string;
    am?: string | null;
    eightHundredOnly?: string | null;
    pm?: string | null;
    after3?: string | null;
    availability?: string | null;
    contactLogNotes?: string | null;
}

// Simple CSV parser that handles quoted fields
export function parseCSV(csvContent: string): string[][] {
    const lines = csvContent.split("\n").filter((line) => line.trim() !== "");
    const rows: string[][] = [];

    for (const line of lines) {
        const row: string[] = [];
        let currentField = "";
        let insideQuotes = false;

        for (let i = 0; i < line.length; i++) {
            const char = line[i];

            if (char === '"') {
                insideQuotes = !insideQuotes;
            } else if (char === "," && !insideQuotes) {
                row.push(currentField.trim());
                currentField = "";
            } else {
                currentField += char;
            }
        }

        // Add the last field
        row.push(currentField.trim());
        rows.push(row);
    }

    return rows;
}

// Convert CSV row to WaitlistEntry object
export function rowToWaitlistEntry(row: string[]): WaitlistRow {
    return {
        currentPastSiblingOfClient: row[0] || null,
        pt: row[1] || null,
        ot: row[2] || null,
        st: row[3] || null,
        referralDate: row[4] || "01/01/1900",
        patientName: row[5] || "Unknown Unknown",
        dob: row[6] || "01/01/1900",
        weight: row[8] || "",
        caregivers: row[9] || "Unknown Unknown",
        // If there's no email put a dummy email down with a uuid so they will be unique and not match!
        // If dummy emails match, then patients, queue entries, etc... are linked incorrectly by the same, dummy caregiver!
        emails: row[10] || `unknown.${faker.string.uuid()}@test.test`,
        phoneNumbers: row[11] || "(000) 000-0000",
        am: row[12] || null,
        eightHundredOnly: row[13] || null,
        pm: row[14] || null,
        after3: row[15] || null,
        availability: row[16] || null,
        contactLogNotes: row[17] || null
    };
}

export function createUnifiedPriorityEntryRow(row: WaitlistRow): {
    entry: UnifiedPriorityEntryInput;
    notes: string[];
} {
    const {
        currentPastSiblingOfClient,
        caregivers,
        patientName,
        referralDate,
        dob,
        weight,
        emails,
        phoneNumbers,
        am,
        eightHundredOnly,
        pm,
        after3,
        availability,
        contactLogNotes,
        pt,
        st,
        ot
    } = row;

    // Get Tags from the row
    const tags: string[] = [];
    if (currentPastSiblingOfClient?.trim() === "x") {
        tags.push("Current Past Sibling of Client");
    }

    if (weight?.trim()) {
        if (weight.includes("lb") || weight.includes("lbs")) {
            tags.push("Weight: " + weight);
        } else {
            tags.push("Weight: " + weight + " lbs");
        }
    }

    if (am?.trim() === "x") {
        tags.push("AM");
    }

    if (pm?.trim() === "x") {
        tags.push("PM");
    }

    if (eightHundredOnly?.trim() === "x") {
        tags.push("8:00 Only");
    }

    if (after3?.trim() === "x") {
        tags.push("After 3PM");
    }

    // Get Notes from row
    const notes: string[] = [];
    if (availability?.trim()) {
        notes.push("Availability: " + availability);
    }
    if (contactLogNotes?.trim()) {
        notes.push("Contact Log Notes: " + contactLogNotes);
    }

    // Caregiver Info
    const caregiverInfo = parseNames(caregivers || "");
    tags.push(...caregiverInfo.tags);
    const email = emails?.split("/")[0].trim();
    const phoneNumber = formatPhoneNumber(phoneNumbers);

    // Patient Info
    const patientInfo = parseNames(patientName || "");
    tags.push(...patientInfo.tags);
    const dateOfBirth = new Date(dob).toISOString();
    const inquiryDate = new Date(referralDate);

    // Therapies
    const therapyCategoryIds: string[] = [];
    if (pt?.trim() === "x") {
        therapyCategoryIds.push("fabb0e91-cbf8-44f9-b4ee-45b31a645dc5");
    }
    if (ot?.trim() === "x") {
        therapyCategoryIds.push("cad5c935-3f30-424a-b503-01e8aa634fd2");
    }
    if (st?.trim() === "x") {
        therapyCategoryIds.push("667b49cf-be70-48fc-a009-9d25a14975af");
    }

    return {
        entry: {
            caregiver: {
                firstName: caregiverInfo.firstNames,
                lastName: caregiverInfo.lastName,
                email: email,
                phoneNumber: phoneNumber
            },
            patient: {
                caregiverId: "",
                firstName: patientInfo.firstNames,
                lastName: patientInfo.lastName,
                dateOfBirth: dateOfBirth,
                postalCode: "46032",
                therapyCategoryIds
            },
            inquiredAt: inquiryDate.toISOString(),
            priorityStatusId: "bf3aa36e-356e-4e57-8236-8b01386e7c06",
            skipEmail: true,
            tags: tags
        },
        notes: notes
    };
}

// Parse caregiver names like "Kelly and David Andrew (Parents)"
function parseNames(nameString: string): {
    firstNames: string;
    lastName: string;
    tags: string[];
} {
    const result = {
        firstNames: "",
        lastName: "",
        tags: [] as string[]
    };

    // Extract tags from parentheses
    const tagMatch = nameString.match(/\(([^)]+)\)/);
    if (tagMatch) {
        const tagText = tagMatch[1].trim();
        // Split multiple tags if they exist (e.g., "Parents, Guardian")
        const tags = tagText.split(",").map((tag) => tag.trim());
        result.tags.push(...tags);

        // Remove the parentheses part from the string for name parsing
        nameString = nameString.replace(/\([^)]+\)/, "").trim();
    }

    const lastName = nameString.split(" ").pop()?.trim();
    if (lastName) {
        result.lastName = lastName;
    }

    const firstNames = nameString.split(" ").slice(0, -1).join(" ");
    if (firstNames) {
        result.firstNames = firstNames;
    }

    return result;
}
